import { useQuery, type QueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { type ReactNode, useEffect, useState } from "react";

import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { ErrorHandler } from "@/web/lib/errors";
import { callVerifyCatskillsExplorePassword } from "@/web/lib/spacetime-server";
import { isAuthError } from "@/web/lib/spacetimedbErrors";
import { PasswordGate } from "@/web/routes/-shareableCardGate/shareableCardGate";

// ===== server fn =====
//
// Runs server-side during SSR (Worker → SpacetimeDB HTTP) and is RPC'd from the
// client for password-gated retries. `callVerifyCatskillsExplorePassword` lives
// in a `@tanstack/react-start/server-only` module, so the start plugin strips
// its implementation from the client bundle and replaces this handler with a
// network stub at build time. Resolves on success; rejects with the server's
// `[NOT_AUTHORIZED]`-tagged error on a bad password.
export const verifyCatskillsPasswordServerFn = createServerFn({ method: "POST" })
  .inputValidator((d: { password: string }) => d)
  .handler(async ({ data }) => {
    await callVerifyCatskillsExplorePassword(data.password);
    return { ok: true } as const;
  });

const queryKey = (password: string) => ["catskills-explore-password", password] as const;

// ===== password persistence =====
const STORAGE_KEY = "catskills-explore-password";

function readPersistedPassword(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? "";
  } catch (error: unknown) {
    ErrorHandler.logError(error);
    return "";
  }
}

function persistPassword(password: string) {
  try {
    window.localStorage.setItem(STORAGE_KEY, password);
  } catch (error: unknown) {
    ErrorHandler.logError(error);
  }
}

function clearPersistedPassword() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error: unknown) {
    ErrorHandler.logError(error);
  }
}

// ===== loader helper =====
//
// Preloads the empty-password verification into the query cache so SSR can
// render the gate synchronously. The empty password never matches, so this
// always resolves to the auth-error state — but it primes the cache shape and
// avoids a client-side loading flash before the gate appears.
export async function prefetchCatskillsGate(queryClient: QueryClient) {
  await queryClient
    .ensureQueryData({
      queryKey: queryKey(""),
      queryFn: () => verifyCatskillsPasswordServerFn({ data: { password: "" } }),
      retry: false,
    })
    .catch((error: unknown) => {
      if (isAuthError(error)) return;
      ErrorHandler.logError(error);
    });
}

// ===== gate wrapper =====
//
// Renders `children` once the persisted/entered password is verified. Shows
// <PasswordGate /> until then. Mirrors `ShareableCardGate` but checks a single
// shared password (no row lookup) against the `verify_catskills_explore_password`
// procedure.
export function CatskillsPasswordGate({ children }: { children: ReactNode }) {
  // Initialize empty to keep SSR and the first client render in sync; pull the
  // persisted password from localStorage after mount.
  const [password, setPassword] = useState("");
  useEffect(() => {
    const stored = readPersistedPassword();
    if (stored) setPassword(stored);
  }, []);

  const verifyQuery = useQuery({
    queryKey: queryKey(password),
    queryFn: () => verifyCatskillsPasswordServerFn({ data: { password } }),
    retry: false,
  });

  useEffect(() => {
    if (verifyQuery.isSuccess && password.length > 0) {
      persistPassword(password);
    }
  }, [verifyQuery.isSuccess, password]);

  useEffect(() => {
    if (isAuthError(verifyQuery.error)) {
      clearPersistedPassword();
    }
  }, [verifyQuery.error]);

  if (verifyQuery.isSuccess) return <>{children}</>;
  if (isAuthError(verifyQuery.error)) {
    return <PasswordGate onSubmit={setPassword} hasError={password.length > 0} />;
  }
  // Any other error (transport, secret unset → also auth error) leaves the gate
  // closed; show the loader only while the first verification is in flight.
  if (verifyQuery.isError) {
    return <PasswordGate onSubmit={setPassword} hasError={password.length > 0} />;
  }
  return <FullPageLoader />;
}
